export const ADMIN_USER = 'admin';
export const SUPER_ADMIN_USER = 'super-admin';
export const SCORCHSOFT_USER = 'scorchsoft-admin';
export const SECURITY_USER = 'security';
export const APPROVER_USER = 'approver';
export const REQUESTER_USER = 'requester';
export const ADMIN_ACCESS_LEVELS = [SCORCHSOFT_USER, ADMIN_USER, SUPER_ADMIN_USER];

export const GUARDING_TYPE_RETAIL = 'retail';
export const GUARDING_TYPE_NON_RETAIL = 'non_retail';

export const REQUEST_TYPE_VISITOR = 'visitor_request';
export const REQUEST_TYPE_ACCESS = 'access_request';
export const REQUEST_TYPE_GUARDING = 'guarding_request';

export type RequestType =
    | typeof REQUEST_TYPE_VISITOR
    | typeof REQUEST_TYPE_ACCESS
    | typeof REQUEST_TYPE_GUARDING;

export const PENDING_COST_APPROVAL = 'Pending Cost Approval';

/**
 * Visit request status ids — mirrors `App\Common\Models\VisitRequest::STATUS_*` on the API
 * (`vfm-api/app/Common/Models/VisitRequest.php`). Keys are strings because the dashboard
 * filter dropdown round-trips them through URL query params.
 */
export const VISIT_REQUEST_STATUS_ID = {
    DRAFT: '0',
    PENDING_NSIP_REVIEW: '3',
    PENDING_COST_APPROVAL: '5',
    PENDING_APPROVAL: '10',
    APPROVED_PENDING_SCHEDULE: '20',
    APPROVED_SCHEDULED: '30',
    COMPLETED: '40',
    QUERIED: '50',
    REJECTED: '60',
    REQUIRES_AMEND: '70',
    CANCELLED: '80',
    SOFT_DELETED: '90',
} as const;

/** Email domains that bypass the cost approval email requirement */
export const COST_APPROVAL_EXEMPT_DOMAINS = ['@vodafone.com', '@vodafonethree.com', '@mitie.com'];

/** Check if an email address belongs to a cost-approval-exempt domain */
export const isCostApprovalExemptEmail = (email: string): boolean => COST_APPROVAL_EXEMPT_DOMAINS.some((domain) => email.toLowerCase().endsWith(domain));

export const getVisitorRequestStatus = (requestType = 'visitor') => ({
    rejected: {
        statusId: 2,
        title: 'Reject Request',
        subTitle: 'Reason for rejection',
        description: `You are about to reject this ${requestType} request. Please enter the reason for the rejection below. The requester will then be able to make amendments to the request and resubmit the form.`,
        confirmButton: 'Reject Request',
        cancelButton: 'Close',
        action: 'reject',
        placeholder: 'Please enter your reasoning for rejecting the request',
    },
    query: {
        statusId: 2,
        title: 'Query Request',
        subTitle: 'Question for requester',
        confirmButton: 'Send',
        cancelButton: 'Close',
        action: 'query',
        placeholder: 'Please enter your query',
    },
    confirm_approval: {
        statusId: 3,
        title: 'Confirm Approval',
        description: `Are you sure you want to approve this ${requestType} request?`,
        confirmButton: 'Approve Request',
        cancelButton: 'Return to Request',
        action: 'approve',
    },
    cancel: {
        statusId: 2,
        title: 'Cancel Request',
        subTitle: 'Reason for cancellation',
        description: 'You are about to cancel this request. Please enter the reason for the cancelation below. Cancelling the request is permanent and a new request will have to be submitted if you change your mind.',
        confirmButton: 'Cancel Request',
        cancelButton: 'Return to Request',
        action: 'cancel',
        placeholder: 'Please enter your reasoning for cancelling the request',
    },
    cost_approval: {
        statusId: 1,
        title: 'Cost Approval',
        description: `Are you sure you want to approve this ${requestType} request?`,
        confirmButton: 'Approve Request',
        cancelButton: 'Return to Request',
        action: 'cost_approve',
    },
});

export const VISITOR_REQUEST_STATUS = getVisitorRequestStatus();

export const mockViewData = {
    selectedSite: {
        id: 1,
        name: 'Selected Site',
        details: [
            {
                id: 1,
                label: 'Site Type',
                value: 'MTX',
            },
            {
                id: 2,
                label: 'Site Address:',
                value: '23 Street Name, Birmingham, B1 223',
            },
        ],
    },
    visitorsAdded: {
        id: 2,
        name: 'Visitors Added',
        type: 'list',
        details: [{
            id: 1,
            value: 'John Smith',
        },
        {
            id: 2,
            value: 'John Taylor',
        },
        {
            id: 3,
            value: 'Lucy Jones',
        },
        {
            id: 4,
            value: 'John Smith',
        },
        {
            id: 5,
            value: 'John Taylor',
        },
        {
            id: 6,
            value: 'Lucy Jones',
        },
        {
            id: 11,
            value: 'John Smith',
        },
        {
            id: 12,
            value: 'John Taylor',
        },
        {
            id: 13,
            value: 'Lucy Jones',
        },
        {
            id: 14,
            value: 'John Smith',
        },
        {
            id: 15,
            value: 'John Taylor',
        }],
    },
    dates: {
        id: 3,
        name: 'Dates & Times',
        type: 'accordion',
        details: [{
            id: 1,
            label: 'Day Shift',
            value: 'Thursday 5th March',
            timeSlot: '09:00 - 17:00',
        },
        {
            id: 2,
            label: 'Night Shift',
            value: 'Thursday 15th March',
            timeSlot: '19:00 - 07:00',
        },
        {
            id: 3,
            label: '24/7',
            shiftStart: '12:00',
            shiftEnd: '18:00',
            value: ['Thursday 5th March',
                'Thursday 15th March',
                'Thursday 25th March',
                'Friday 6th March',
                'Sunday 18th March'],
        }],
    },
    work: {
        id: 4,
        name: 'Work Type',
        details: [{
            id: 1,
            label: 'Work Types:',
            value: 'Parent Work Type',
        }, {
            id: 2,
            label: 'Description of Works:',
            value: 'Lorem ipsum dolor sit amet consectetur. Sed facilisis laoreet felis et eleifend pellentesque. Aliquam amet tellus cursus dui cras penatibus.',
        }],
    },
    comments: {
        id: 5,
        name: 'Comments',
        type: 'comment',
        details: [{
            id: 1,
            label: 'John Smith',
            value: 'Lorem ipsum dolor sit amet consectetur. Sed facilisis laoreet felis et eleifend pellentesque. Aliquam amet tellus cursus dui cras penatibus.',
            time: '01/02/2023 at 11:32',
        }],
    },
};
